import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {environment} from '../../../environments/environment';
import {SecurityUtils} from './security.utils';
import {finalize, map, share, tap} from 'rxjs/operators';
import {JwtHelperService} from '@auth0/angular-jwt';

@Injectable({providedIn: 'root'})
export class AuthenticationService {
	private httpClient = inject(HttpClient);
	private jwtHelper = new JwtHelperService();
	// In-flight refresh shared by every 401 that lands while it runs: one call to the engine, not one per request.
	private refresh$: Observable<string> | null = null;

	signin(createToken: CreateToken): Observable<boolean> {
		return this.httpClient.post<CreateTokenResponse>(environment.engineURL + '/authenticate', JSON.stringify(createToken), {headers: SecurityUtils.getHttpHeaders()})
			.pipe(map(tokenResponse => this.storeTokens(tokenResponse)));
	}

	/**
	 * Exchanges the stored refresh token (90 days, engine-side) for a new access token.
	 * Errors when there is no refresh token or the engine rejects it: the caller must then log out.
	 */
	refreshAccessToken(): Observable<string> {
		if (this.refresh$ === null) {
			const refreshToken = SecurityUtils.getRefreshToken();
			if (refreshToken === null) {
				return throwError(() => new Error('No refresh token'));
			}
			this.refresh$ = this.httpClient.post<CreateTokenResponse>(environment.engineURL + '/authenticate/refresh', JSON.stringify({refreshToken}), {headers: SecurityUtils.getHttpHeaders()})
				.pipe(
					map(response => {
						// storeTokens also keeps a rotated refresh token if the engine ever starts sending one.
						this.storeTokens(response);
						return response.accessToken;
					}),
					finalize(() => this.refresh$ = null),
					share(),
				);
		}
		return this.refresh$;
	}

	private storeTokens(tokenResponse: CreateTokenResponse): boolean {
		SecurityUtils.setAuthToken(tokenResponse.accessToken);
		if (tokenResponse.refreshToken) {
			SecurityUtils.setRefreshToken(tokenResponse.refreshToken);
		}
		return true;
	}

	checkPin(createTokenPin: CreateTokenPin): Observable<ValidateAccountPinResponse> {
		return this.httpClient.post<ValidateAccountPinResponse>(environment.engineURL + '/authenticate-pin', JSON.stringify(createTokenPin), {headers: SecurityUtils.getHttpHeaders()})
			.pipe(tap(tokenResponse => {
				if (tokenResponse?.tokens?.accessToken) {
					this.storeTokens(tokenResponse.tokens);
				}
			}));
	}

	microsoftSigning(idToken: string, expiresAt: Date | null): Observable<boolean> {
		return this.httpClient.post<CreateTokenResponse>(environment.engineURL + '/authenticate/microsoft', JSON.stringify({
			'idToken': idToken,
			'expiresAt': expiresAt
		}), {headers: SecurityUtils.getHttpHeaders()})
			.pipe(map(tokenResponse => this.storeTokens(tokenResponse)));
	}

	hasValidSessionSnapshot(): boolean {
		const token = SecurityUtils.getAuthToken();

		if (token === null) {
			return false;
		}

		try {
			// An expired access token is still a session when a refresh token is stored: the interceptor
			// renews it on the first 401. The refresh token is opaque, so its own validity (expired,
			// revoked, deactivated account) is only known once the engine answers that first refresh;
			// a rejected one then logs the user out from the interceptor. Without one, the user must
			// reach the login form.
			return !this.jwtHelper.isTokenExpired(token) || SecurityUtils.getRefreshToken() !== null;
		} catch {
			return false;
		}
	}

	logout(): void {
		// Clear the token first: Intercom may not be loaded yet (401 on app start), and a throw
		// here used to abort the sign-out, leaving the user stuck on a blank authenticated page.
		SecurityUtils.removeAuthToken();
		const intercom = (window as unknown as { Intercom?: (command: string) => void }).Intercom;
		if (environment.production && typeof intercom === 'function') {
			// Remove intercom cookie
			intercom('shutdown');
		}
	}

	getUserInfo(): UserInfo | null {
		const token = SecurityUtils.getAuthToken();

		if (token) {
			const decodedToken = this.jwtHelper.decodeToken(token);
			return {
				id: decodedToken.id,
				firstName: decodedToken.firstname,
				lastName: decodedToken.lastname,
				email: decodedToken.upn
			};
		} else {
			return null;
		}
	}
}

export interface CreateToken {
	email: string;
	password: string;
}

export interface CreateTokenPin {
	email: string;
	pin: string;
}

export interface CreateMicrosoftToken {
	accessToken: string;
	refreshToken: string;
	refreshTokenExpiration: string;
	mail: string;
	oid: string;
}

export interface PinCodeError {
	error: string;
	remainingAttempt?: number;
	expiredAt?: string
}

export interface CreateTokenResponse {
	accessToken: string;
	refreshToken?: string;
}

export interface ValidateAccountPinResponse {
	tokens?: CreateTokenResponse;
	error?: PinCodeError
}


export interface UserInfo {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
}
